import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Gift, Users, Wallet, ArrowRight, Check, Crown, 
  Share2, Sparkles, TrendingUp, Shield, Zap,
  Twitter, Send, MessageCircle, Copy
} from 'lucide-react';
import SEO from '../components/SEO';

const ReferralProgramPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleGetStarted = () => {
    if (user) {
      navigate('/referans');
    } else {
      navigate('/register');
    }
  };

  return (
    <div className="min-h-screen bg-brand-dark">
      <SEO 
        title="Referans Programı | Borsamex"
        description="Arkadaşlarınızı Borsamex'e davet edin, birlikte kazanın! Siz %20 kredi, arkadaşınız %15 indirim."
      />

      {/* Hero Section */}
      <section className="relative overflow-hidden py-16 md:py-24">
        {/* Background effects */}
        <div className="absolute inset-0 bg-gradient-to-b from-primary/10 via-transparent to-transparent" />
        <div className="absolute top-20 left-1/4 w-72 h-72 bg-primary/20 rounded-full blur-[100px]" />
        <div className="absolute bottom-20 right-1/4 w-72 h-72 bg-emerald-500/20 rounded-full blur-[100px]" />
        
        <div className="container mx-auto px-6 relative">
          <div className="text-center max-w-3xl mx-auto">
            <Badge className="mb-6 bg-primary/20 text-primary border-primary/50 text-sm px-4 py-1.5">
              <Gift className="h-4 w-4 mr-2" />
              Kupon Kardeşliği
            </Badge>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Arkadaşını Davet Et,
              <br />
              <span className="text-gradient">Birlikte Kazanın!</span>
            </h1>
            
            <p className="text-lg md:text-xl text-slate-400 mb-8 max-w-2xl mx-auto">
              Borsamex&apos;i arkadaşlarınla paylaş. Onlar Pro üye olduğunda sen de kazan!
            </p>

            {/* Value Props */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-10">
              <div className="flex items-center gap-2 bg-primary/10 border border-primary/30 rounded-full px-5 py-2.5">
                <Wallet className="h-5 w-5 text-primary" />
                <span className="text-white font-semibold">Sen %20 Kredi Kazan</span>
              </div>
              <div className="flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 rounded-full px-5 py-2.5">
                <Crown className="h-5 w-5 text-amber-400" />
                <span className="text-white font-semibold">Arkadaşın %15 İndirim</span>
              </div>
            </div>

            <Button 
              size="lg" 
              onClick={handleGetStarted}
              className="bg-primary hover:bg-primary/90 text-lg px-8 py-6"
            >
              {user ? 'Referans Kodumu Göster' : 'Hemen Başla'}
              <ArrowRight className="h-5 w-5 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-slate-900/50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Nasıl Çalışır?</h2>
            <p className="text-slate-400">3 basit adımda kazanmaya başla</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {/* Step 1 */}
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center text-2xl font-bold text-primary">
                1
              </div>
              <Card className="pt-8 h-full">
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Share2 className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Kodunu Paylaş</h3>
                  <p className="text-slate-400 text-sm">
                    Sana özel referans kodunu veya linkini arkadaşlarınla paylaş
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Step 2 */}
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center text-2xl font-bold text-primary">
                2
              </div>
              <Card className="pt-8 h-full">
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 bg-amber-500/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Users className="h-8 w-8 text-amber-400" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Arkadaşın Katılsın</h3>
                  <p className="text-slate-400 text-sm">
                    Arkadaşın kodunla kayıt olsun ve Pro üyelik satın alsın
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Step 3 */}
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center text-2xl font-bold text-primary">
                3
              </div>
              <Card className="pt-8 h-full">
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 bg-green-500/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Wallet className="h-8 w-8 text-green-400" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Kredi Kazan</h3>
                  <p className="text-slate-400 text-sm">
                    Arkadaşının ödemesinden %20 cüzdanına kredi olarak eklensin
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-5xl mx-auto">
            {/* Left - For Referrer */}
            <div>
              <Badge className="mb-4 bg-primary/20 text-primary border-primary/50">
                Davet Eden İçin
              </Badge>
              <h2 className="text-3xl font-bold mb-6">
                Her Davet <span className="text-primary">Kazandırır</span>
              </h2>
              
              <div className="space-y-4">
                <div className="flex items-start gap-4 p-4 bg-slate-800/50 rounded-xl">
                  <div className="p-2 bg-primary/20 rounded-lg">
                    <Wallet className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">%20 Komisyon</h4>
                    <p className="text-sm text-slate-400">
                      Arkadaşın ödeme yaptığında, tutarın %20&apos;si cüzdanına eklenir
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4 p-4 bg-slate-800/50 rounded-xl">
                  <div className="p-2 bg-primary/20 rounded-lg">
                    <TrendingUp className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Sınırsız Davet</h4>
                    <p className="text-sm text-slate-400">
                      İstediğin kadar kişi davet et, her biri için kazan
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4 p-4 bg-slate-800/50 rounded-xl">
                  <div className="p-2 bg-primary/20 rounded-lg">
                    <Zap className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Anında Kredi</h4>
                    <p className="text-sm text-slate-400">
                      Ödeme onaylandığı anda kredi hesabına yansır
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right - For Referred */}
            <div>
              <Badge className="mb-4 bg-amber-500/20 text-amber-400 border-amber-500/50">
                Davet Edilen İçin
              </Badge>
              <h2 className="text-3xl font-bold mb-6">
                İlk Adımda <span className="text-amber-400">%15 İndirim</span>
              </h2>
              
              <div className="space-y-4">
                <div className="flex items-start gap-4 p-4 bg-slate-800/50 rounded-xl">
                  <div className="p-2 bg-amber-500/20 rounded-lg">
                    <Crown className="h-5 w-5 text-amber-400" />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">%15 İndirim</h4>
                    <p className="text-sm text-slate-400">
                      İlk Pro üyelik ödemende %15 indirim al
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4 p-4 bg-slate-800/50 rounded-xl">
                  <div className="p-2 bg-amber-500/20 rounded-lg">
                    <Shield className="h-5 w-5 text-amber-400" />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Güvenilir Kaynak</h4>
                    <p className="text-sm text-slate-400">
                      Arkadaşının önerisiyle güvenle başla
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4 p-4 bg-slate-800/50 rounded-xl">
                  <div className="p-2 bg-amber-500/20 rounded-lg">
                    <Gift className="h-5 w-5 text-amber-400" />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Sen de Davet Et</h4>
                    <p className="text-sm text-slate-400">
                      Kayıt olunca sen de kendi kodunu al ve paylaş
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Example Calculation */}
      <section className="py-16 bg-slate-900/50">
        <div className="container mx-auto px-6">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-8">Örnek Hesaplama</h2>
            
            <Card className="bg-gradient-to-br from-primary/10 to-emerald-500/10 border-primary/30">
              <CardContent className="p-8">
                <div className="grid grid-cols-2 gap-6 mb-6">
                  <div className="text-left">
                    <p className="text-sm text-slate-400 mb-1">Arkadaşın ödemesi</p>
                    <p className="text-2xl font-bold">₺590</p>
                    <p className="text-xs text-slate-500">Aylık Pro üyelik</p>
                  </div>
                  <div className="text-left">
                    <p className="text-sm text-slate-400 mb-1">Arkadaşın indirimi</p>
                    <p className="text-2xl font-bold text-amber-400">-₺89</p>
                    <p className="text-xs text-slate-500">%15 indirim</p>
                  </div>
                </div>
                
                <div className="border-t border-primary/30 pt-6">
                  <p className="text-sm text-slate-400 mb-2">Senin kazancın</p>
                  <p className="text-4xl font-bold text-primary">₺100</p>
                  <p className="text-sm text-slate-400 mt-1">
                    (₺501 x %20 = ₺100 cüzdan kredisi)
                  </p>
                </div>
                
                <div className="mt-6 p-4 bg-slate-800/50 rounded-lg">
                  <p className="text-sm text-slate-300">
                    💡 5 arkadaşını davet etsen = <span className="text-primary font-bold">₺500 kredi</span>
                    <br />
                    <span className="text-xs text-slate-500">Neredeyse 1 aylık üyelik bedava!</span>
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Share Methods */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Nasıl Paylaşırsın?</h2>
            <p className="text-slate-400">En sevdiğin platformda paylaş</p>
          </div>

          <div className="flex flex-wrap justify-center gap-4 max-w-2xl mx-auto">
            <div className="flex items-center gap-3 bg-slate-800/50 border border-slate-700 rounded-xl px-6 py-4">
              <Twitter className="h-6 w-6 text-sky-400" />
              <span>Twitter</span>
            </div>
            <div className="flex items-center gap-3 bg-slate-800/50 border border-slate-700 rounded-xl px-6 py-4">
              <Send className="h-6 w-6 text-blue-400" />
              <span>Telegram</span>
            </div>
            <div className="flex items-center gap-3 bg-slate-800/50 border border-slate-700 rounded-xl px-6 py-4">
              <MessageCircle className="h-6 w-6 text-green-400" />
              <span>WhatsApp</span>
            </div>
            <div className="flex items-center gap-3 bg-slate-800/50 border border-slate-700 rounded-xl px-6 py-4">
              <Copy className="h-6 w-6 text-slate-400" />
              <span>Link Kopyala</span>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-slate-900/50">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">Sık Sorulan Sorular</h2>
            
            <div className="space-y-4">
              <details className="group">
                <summary className="flex items-center justify-between p-4 bg-slate-800/50 border border-slate-700 rounded-xl cursor-pointer hover:bg-slate-800 transition-colors">
                  <span className="font-medium">Krediyi nasıl kullanabilirim?</span>
                  <span className="text-slate-400 group-open:rotate-180 transition-transform">▼</span>
                </summary>
                <div className="px-4 py-3 text-sm text-slate-400">
                  Cüzdanındaki kredi, bir sonraki Pro üyelik yenilemende otomatik olarak düşülür. 
                  Örneğin ₺100 kredin varsa ve aylık üyelik ₺590 ise, sadece ₺490 ödersin.
                </div>
              </details>

              <details className="group">
                <summary className="flex items-center justify-between p-4 bg-slate-800/50 border border-slate-700 rounded-xl cursor-pointer hover:bg-slate-800 transition-colors">
                  <span className="font-medium">Krediyi nakde çevirebilir miyim?</span>
                  <span className="text-slate-400 group-open:rotate-180 transition-transform">▼</span>
                </summary>
                <div className="px-4 py-3 text-sm text-slate-400">
                  Şu an için kredi sadece üyelik ödemelerinde kullanılabilir. 
                  İleride &quot;Borsamex Partner&quot; programı ile nakit çekim seçeneği eklenecek.
                </div>
              </details>

              <details className="group">
                <summary className="flex items-center justify-between p-4 bg-slate-800/50 border border-slate-700 rounded-xl cursor-pointer hover:bg-slate-800 transition-colors">
                  <span className="font-medium">İndirim sadece ilk ödeme için mi?</span>
                  <span className="text-slate-400 group-open:rotate-180 transition-transform">▼</span>
                </summary>
                <div className="px-4 py-3 text-sm text-slate-400">
                  Evet, %15 indirim sadece yeni kullanıcının ilk Pro üyelik ödemesinde geçerlidir.
                </div>
              </details>

              <details className="group">
                <summary className="flex items-center justify-between p-4 bg-slate-800/50 border border-slate-700 rounded-xl cursor-pointer hover:bg-slate-800 transition-colors">
                  <span className="font-medium">Komisyonumu ne zaman alırım?</span>
                  <span className="text-slate-400 group-open:rotate-180 transition-transform">▼</span>
                </summary>
                <div className="px-4 py-3 text-sm text-slate-400">
                  Arkadaşının ödemesi onaylandığı anda %20 komisyon otomatik olarak cüzdanına eklenir.
                </div>
              </details>

              <details className="group">
                <summary className="flex items-center justify-between p-4 bg-slate-800/50 border border-slate-700 rounded-xl cursor-pointer hover:bg-slate-800 transition-colors">
                  <span className="font-medium">Referans kodumu nereden bulabilirim?</span>
                  <span className="text-slate-400 group-open:rotate-180 transition-transform">▼</span>
                </summary>
                <div className="px-4 py-3 text-sm text-slate-400">
                  Giriş yaptıktan sonra sağ üstteki menüden &quot;Referans Programı&quot;na tıkla. 
                  Orada kişisel kodun ve paylaşım linkini görebilirsin.
                </div>
              </details>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <Sparkles className="h-12 w-12 text-primary mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Hemen Başla, Hemen Kazan!
            </h2>
            <p className="text-lg text-slate-400 mb-8">
              Arkadaşlarını davet et, birlikte trading yaparken birlikte de kazanın.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                onClick={handleGetStarted}
                className="bg-primary hover:bg-primary/90 text-lg px-8"
              >
                {user ? 'Kodumu Göster' : 'Kayıt Ol ve Kodunu Al'}
                <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
              
              {!user && (
                <Button 
                  size="lg" 
                  variant="outline"
                  onClick={() => navigate('/login')}
                  className="text-lg px-8"
                >
                  Zaten Üyeyim
                </Button>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ReferralProgramPage;
